/**
 * Done. CPD — SurveyOverview (Stitch v2 / screen 6)
 *
 * Editorial navigator for the personalisation survey. Lists six numbered
 * sections with status states (complete / in_progress / not_started /
 * locked), a circular progress ring scoped to the four currently-active
 * sections, and a help card at the bottom. Section rows are clickable
 * (apart from locked) so the user can jump straight to any section.
 */

import type { ReactElement } from 'react';

export type SurveyStatus = 'complete' | 'in_progress' | 'not_started' | 'locked';

export interface SurveySectionMeta {
  /** 1-based display number */
  num: number;
  id: string;
  title: string;
  description: string;
  duration: string;
  status: SurveyStatus;
  /** Counted toward progress ring + Generate gating. */
  active: boolean;
}

interface SurveyOverviewProps {
  sections: SurveySectionMeta[];
  onSelect: (id: string) => void;
  onGenerate: () => void;
  canGenerate: boolean;
  onSkip: () => void;
}

const STATUS_BADGE: Record<SurveyStatus, { label: string; className: string; icon?: string }> = {
  complete: {
    label: 'Complete',
    icon: 'check_circle',
    className: 'bg-[var(--stitch-primary)]/10 text-[var(--stitch-primary)]',
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-[var(--stitch-secondary-container)] text-[var(--stitch-on-secondary-container)]',
  },
  not_started: {
    label: 'Not Started',
    className: 'border border-[var(--stitch-outline-variant)] text-[var(--stitch-outline)]',
  },
  locked: {
    label: 'Coming Soon',
    className: 'border border-dashed border-[var(--stitch-outline-variant)] text-[var(--stitch-outline)]',
  },
};

function StatusBadge({ status }: { status: SurveyStatus }): ReactElement {
  const cfg = STATUS_BADGE[status];
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-tighter ${cfg.className}`}
    >
      {cfg.icon && (
        <span className="material-symbols-outlined text-[14px] is-filled">{cfg.icon}</span>
      )}
      {cfg.label}
    </span>
  );
}

function ProgressRing({ completed, total }: { completed: number; total: number }): ReactElement {
  const radius = 24;
  const circumference = 2 * Math.PI * radius;
  const pct = total > 0 ? completed / total : 0;
  const offset = circumference * (1 - pct);
  const percentLabel = Math.round(pct * 100);

  return (
    <div className="bg-[var(--stitch-surface-container-lowest)] p-5 md:p-6 rounded-2xl border border-[var(--stitch-outline-variant)] flex items-center gap-5 stitch-editorial-shadow">
      <div className="relative w-14 h-14">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 56 56">
          <circle
            cx="28"
            cy="28"
            r={radius}
            fill="transparent"
            stroke="var(--stitch-surface-container-high)"
            strokeWidth={6}
          />
          <circle
            cx="28"
            cy="28"
            r={radius}
            fill="transparent"
            stroke="var(--stitch-primary)"
            strokeWidth={6}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.4s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="stitch-font-label text-[var(--stitch-primary)] text-[11px]">
            {completed}/{total}
          </span>
        </div>
      </div>
      <div>
        <p className="stitch-font-label text-[var(--stitch-on-surface-variant)] uppercase tracking-widest text-[10px] mb-0.5">
          Your Progress
        </p>
        <p className="stitch-font-headline text-[var(--stitch-primary)] text-[20px]">
          {percentLabel}% Completed
        </p>
      </div>
    </div>
  );
}

interface SectionRowProps {
  section: SurveySectionMeta;
  onSelect: (id: string) => void;
}

function SectionRow({ section, onSelect }: SectionRowProps): ReactElement {
  const { num, id, title, description, duration, status } = section;
  const interactive = status !== 'locked';

  const numberBadge = ((): ReactElement => {
    if (status === 'locked') {
      return (
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[var(--stitch-surface-container)] flex items-center justify-center text-[var(--stitch-outline-variant)]">
          <span className="material-symbols-outlined">lock</span>
        </div>
      );
    }
    if (status === 'in_progress') {
      return (
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[var(--stitch-primary)] flex items-center justify-center text-[var(--stitch-on-primary)] stitch-font-headline font-bold shadow-md">
          {num}
        </div>
      );
    }
    if (status === 'complete') {
      return (
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[var(--stitch-primary-fixed)] flex items-center justify-center text-[var(--stitch-primary)] stitch-font-headline font-bold">
          {num}
        </div>
      );
    }
    return (
      <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-[var(--stitch-outline-variant)] flex items-center justify-center text-[var(--stitch-outline)] stitch-font-headline font-bold">
        {num}
      </div>
    );
  })();

  const wrapperClass = ((): string => {
    if (status === 'locked') {
      return 'bg-[var(--stitch-surface-container)]/30 border border-dashed border-[var(--stitch-outline-variant)] opacity-70';
    }
    if (status === 'in_progress') {
      return 'bg-white border-2 border-[var(--stitch-primary)] shadow-lg';
    }
    if (status === 'complete') {
      return 'bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] hover:shadow-md';
    }
    return 'bg-[var(--stitch-surface-container-low)]/60 border border-[var(--stitch-outline-variant)] hover:bg-[var(--stitch-surface-container-low)]';
  })();

  const titleClass = status === 'not_started' || status === 'locked'
    ? 'text-[var(--stitch-outline)]'
    : 'text-[var(--stitch-primary)]';
  const descClass = status === 'not_started' || status === 'locked'
    ? 'text-[var(--stitch-outline)]'
    : 'text-[var(--stitch-on-surface-variant)]';

  const action = ((): ReactElement => {
    if (status === 'locked') {
      return (
        <span className="stitch-font-label text-[var(--stitch-outline-variant)] uppercase tracking-tighter text-[10px]">
          Available soon
        </span>
      );
    }
    if (status === 'in_progress') {
      return (
        <span className="stitch-font-label bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] px-5 py-2 rounded-full inline-flex items-center gap-2 text-[12px] uppercase tracking-widest">
          Continue
          <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
        </span>
      );
    }
    if (status === 'complete') {
      return (
        <span className="stitch-font-label text-[var(--stitch-primary)] border-b border-[var(--stitch-primary)] text-[12px] uppercase tracking-widest">
          Edit
        </span>
      );
    }
    return (
      <span className="material-symbols-outlined text-[var(--stitch-outline)]">chevron_right</span>
    );
  })();

  const content = (
    <>
      {numberBadge}
      <div className="flex-grow text-center md:text-left">
        <div className="flex flex-col md:flex-row md:items-center gap-2 mb-1">
          <h3 className={`stitch-font-headline text-[20px] uppercase ${titleClass}`}>{title}</h3>
          <StatusBadge status={status} />
        </div>
        <p className={`stitch-font-body text-[14px] line-clamp-1 ${descClass}`}>{description}</p>
      </div>
      <div className="flex-shrink-0 flex items-center gap-6">
        <span className="stitch-font-label text-[var(--stitch-outline)] italic text-[12px]">{duration}</span>
        {action}
      </div>
    </>
  );

  const baseClass = `group p-6 md:p-7 rounded-2xl flex flex-col md:flex-row items-center gap-6 transition-all ${wrapperClass}`;

  if (!interactive) {
    return <div className={baseClass}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => { onSelect(id); }}
      className={`${baseClass} text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--stitch-primary)] focus-visible:ring-offset-2 w-full`}
    >
      {content}
    </button>
  );
}

export function SurveyOverview({ sections, onSelect, onGenerate, canGenerate, onSkip }: SurveyOverviewProps): ReactElement {
  const activeSections = sections.filter(s => s.active);
  const completedCount = activeSections.filter(s => s.status === 'complete').length;
  const totalActive = activeSections.length;

  return (
    <div className="flex flex-col gap-10">
      <section className="flex flex-col md:flex-row md:items-end justify-between gap-8">
        <div className="flex-1">
          <h2 className="stitch-font-display text-[40px] md:text-[52px] text-[var(--stitch-primary)] uppercase tracking-tight leading-[1.05] mb-4">
            Personalisation Survey
          </h2>
          <p className="stitch-font-body text-[17px] text-[var(--stitch-on-surface-variant)] leading-relaxed max-w-xl">
            Refine your professional development. This takes about 5 minutes. Complete all six sections below to curate your curriculum and streamline compliance.
          </p>
        </div>
        <ProgressRing completed={completedCount} total={totalActive} />
      </section>

      <section className="space-y-4" aria-label="Survey sections">
        {sections.map(section => (
          <SectionRow key={section.id} section={section} onSelect={onSelect} />
        ))}
      </section>

      <section className="bg-[var(--stitch-surface-container-low)] p-8 rounded-3xl border border-[var(--stitch-outline-variant)] flex flex-col md:flex-row items-center gap-8 stitch-editorial-shadow">
        <div className="w-20 h-20 rounded-2xl bg-[var(--stitch-primary)]/10 flex items-center justify-center flex-shrink-0">
          <span className="material-symbols-outlined text-[var(--stitch-primary)] text-[40px]">support_agent</span>
        </div>
        <div className="text-center md:text-left flex-1">
          <h4 className="stitch-font-headline text-[20px] text-[var(--stitch-primary)] uppercase mb-2">
            Ready to generate your plan?
          </h4>
          <p className="stitch-font-body text-[15px] text-[var(--stitch-on-surface-variant)] mb-6 leading-relaxed">
            Once all six sections are complete we will draft your personalised annual CPD plan.
          </p>
          <div className="flex flex-wrap justify-center md:justify-start gap-3">
            <button
              type="button"
              onClick={onGenerate}
              aria-disabled={!canGenerate || undefined}
              className={`stitch-font-label text-[12px] uppercase tracking-widest px-7 py-3 rounded-full inline-flex items-center gap-2 transition-all ${
                canGenerate
                  ? 'bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] hover:bg-[var(--stitch-primary-container)] stitch-ghost-shadow'
                  : 'bg-[var(--stitch-surface-container)] text-[var(--stitch-outline)] cursor-not-allowed'
              }`}
            >
              Generate My Plan
              <span className="material-symbols-outlined text-[16px]">auto_awesome</span>
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="stitch-font-label text-[12px] uppercase tracking-widest px-6 py-3 rounded-full inline-flex items-center gap-2 transition-all border border-[var(--stitch-outline-variant)] text-[var(--stitch-on-surface-variant)] hover:border-[var(--stitch-outline)] hover:text-[var(--stitch-on-surface)]"
            >
              Skip for now
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
